import { useState } from 'react';

import { useClickAway } from '@uidotdev/usehooks';

import { Permission } from '../../../../../../backend/src/auth/permissions/permission';
import { TrackingEventsV2 } from '@/analytics/eventsV2';
import { trackCustomEvent } from '@/analytics/Mixpanel';
import { useDownloadDocumentV2 } from '@/api/mutations/useDownloadDocumentV2';
import { useDownloadThreadFile } from '@/api/mutations/useDownloadThreadFile';
import { PermissionGate } from '@/hooks/useHasPermission';
import { Icon } from '@/new/components/icon';
import { Button } from '@/new/components/shadcn/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/new/components/shadcn/tooltip';
import { cn } from '@/new/lib/utils';

export interface ThreadFileDownloadContext {
  matterId: string;
  subsetId: string;
  threadId: string;
  fileId: string;
  fileName?: string;
}

export const DownloadIcon = ({
  matterId,
  docId,
  fileName,
  isProcessed,
  fileExtension,
  hideHighlightedDownload,
  threadFile,
}: {
  matterId: string;
  docId: string;
  fileName?: string;
  isProcessed?: boolean;
  fileExtension?: string;
  hideHighlightedDownload: boolean;
  threadFile?: ThreadFileDownloadContext;
}) => {
  if (threadFile) {
    return <ThreadFileDownloadIcon {...threadFile} />;
  }

  return (
    <DocDownloadIcon
      matterId={matterId}
      docId={docId}
      fileName={fileName}
      isProcessed={isProcessed}
      fileExtension={fileExtension}
      hideHighlightedDownload={hideHighlightedDownload}
    />
  );
};

const ThreadFileDownloadIcon = ({ matterId, subsetId, threadId, fileId, fileName }: ThreadFileDownloadContext) => {
  const { mutate: download, isLoading } = useDownloadThreadFile({
    matterId,
    subsetId,
    threadId,
    fileId,
    fileName,
  });

  return (
    <PermissionGate permission={Permission.ThreadDownload} uiMode="disabled">
      <Tooltip>
        <TooltipTrigger asChild>
          <Button variant="blue" size="iconSm" onClick={() => download()} disabled={isLoading}>
            <Icon className={cn('lucide--download', isLoading && 'animate-pulse')} />
          </Button>
        </TooltipTrigger>
        <TooltipContent>
          <p>Download</p>
        </TooltipContent>
      </Tooltip>
    </PermissionGate>
  );
};

const DocDownloadIcon = ({
  matterId,
  docId,
  fileName,
  isProcessed,
  fileExtension,
  hideHighlightedDownload,
}: {
  matterId: string;
  docId: string;
  fileName?: string;
  isProcessed?: boolean;
  fileExtension?: string;
  hideHighlightedDownload: boolean;
}) => {
  const [downloadingHighlighted, setDownloadingHighlighted] = useState<boolean>(false);
  const [downloadingAsPDF, setDownloadingAsPDF] = useState<boolean>(false);
  const [showOptions, setShowOptions] = useState(false);
  const containerRef = useClickAway<HTMLDivElement>(() => setShowOptions(false));

  const { mutate: downloadOriginal, isLoading: isDownloadingOriginal } = useDownloadDocumentV2({
    matterId,
    docId,
    fileName,
    downloadType: 'original',
  });

  const { mutateAsync: downloadHighlighted, isLoading: isDownloadingHighlighted } = useDownloadDocumentV2({
    matterId,
    docId,
    fileName,
    downloadType: 'highlighted',
  });

  const { mutateAsync: downloadAsPDF, isLoading: isDownloadingAsPDF } = useDownloadDocumentV2({
    matterId,
    docId,
    fileName,
    downloadType: 'asPDF',
  });

  const isDownloading = isDownloadingOriginal || isDownloadingHighlighted || isDownloadingAsPDF;

  const handleDownloadOriginal = () => {
    downloadOriginal();
    trackCustomEvent(TrackingEventsV2.DocViewerDownloadDocument, { matterId, docId, type: 'original' });
    setShowOptions(false);
  };

  const handleDownloadAsPDF = async () => {
    setDownloadingAsPDF(true);
    await downloadAsPDF();
    trackCustomEvent(TrackingEventsV2.DocViewerDownloadDocument, { matterId, docId, type: 'asPDF' });
    setDownloadingAsPDF(false);
    setShowOptions(false);
  };

  const handleDownloadHighlighted = async () => {
    setDownloadingHighlighted(true);
    await downloadHighlighted();
    trackCustomEvent(TrackingEventsV2.DocViewerDownloadDocument, { matterId, docId, type: 'highlighted' });
    setDownloadingHighlighted(false);
    setShowOptions(false);
  };

  const toggleOptions = () => {
    setShowOptions(!showOptions);
  };

  return (
    <PermissionGate permission={Permission.DocumentDownload} uiMode="disabled">
      <div className="relative flex items-center" ref={containerRef}>
        <Tooltip open={showOptions ? false : undefined}>
          <TooltipTrigger asChild>
            <Button
              variant="blue"
              size="iconSm"
              onClick={toggleOptions}
              disabled={isDownloading}
              className={cn(showOptions ? 'bg-sky-100' : '')}
            >
              <Icon className={cn('lucide--download', isDownloading && 'animate-pulse')} />
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            <p>Download</p>
          </TooltipContent>
        </Tooltip>

        <div
          className={cn(
            'absolute top-full left-1/2 z-10 mt-2 flex -translate-x-1/2 gap-1',
            showOptions ? 'pointer-events-auto' : 'pointer-events-none',
          )}
        >
          <Button
            variant="blue"
            size="sm"
            onClick={handleDownloadOriginal}
            disabled={isDownloadingOriginal}
            className={cn(
              'h-7 gap-1 px-2 text-[11px] whitespace-nowrap transition-all duration-200',
              showOptions ? 'translate-y-0 scale-100 opacity-100' : '-translate-y-2 scale-95 opacity-0',
            )}
            style={{
              transitionDelay: showOptions ? '0ms' : '50ms',
            }}
          >
            <Icon className="lucide--file-text h-3 w-3" />
            <span>Original</span>
          </Button>

          {isProcessed && fileExtension?.toLowerCase() !== 'pdf' && (
            <Button
              variant="blue"
              size="sm"
              onClick={handleDownloadAsPDF}
              disabled={isDownloadingAsPDF}
              className={cn(
                'h-7 gap-1 px-2 text-[11px] whitespace-nowrap transition-all duration-200',
                showOptions ? 'translate-y-0 scale-100 opacity-100' : '-translate-y-2 scale-95 opacity-0',
              )}
              style={{
                transitionDelay: showOptions ? '50ms' : '50ms',
              }}
            >
              {downloadingAsPDF ? (
                <Icon className="lucide--loader-circle h-3 w-3 animate-spin duration-700" />
              ) : (
                <Icon className="lucide--file-text h-3 w-3" />
              )}
              <span>As PDF</span>
            </Button>
          )}

          {!hideHighlightedDownload && (
            <Button
              variant="blue"
              size="sm"
              onClick={handleDownloadHighlighted}
              disabled={isDownloadingHighlighted}
              className={cn(
                'h-7 gap-1 px-2 text-[11px] whitespace-nowrap transition-all duration-200',
                showOptions ? 'translate-y-0 scale-100 opacity-100' : '-translate-y-2 scale-95 opacity-0',
              )}
              style={{
                transitionDelay: showOptions ? '50ms' : '0ms',
              }}
            >
              {downloadingHighlighted ? (
                <Icon className="lucide--loader-circle h-3 w-3 animate-spin duration-700" />
              ) : (
                <Icon className="lucide--highlighter h-3 w-3" />
              )}
              <span>Highlighted</span>
            </Button>
          )}
        </div>
      </div>
    </PermissionGate>
  );
};
